/**
 * Lakshmi footprints — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_diwali`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const lakshmiFootprintsPack: ToilePack = {
    slug: 'lakshmi-footprints',
    category: 'faith',
    name: 'Lakshmi footprints',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'pair', label: 'Pair of feet', subject: 'a pair of small footprints with a lotus inside each sole and dotted toes, facing up the page as if walking in'},
        {key: 'path', label: 'Path inside', subject: 'a path of six footprints climbing from the bottom of the page to a doorway at the top, each with a small swastik in the heel'},
        {key: 'alpana', label: 'Alpana feet', subject: 'a Bengali alpana pair of feet in flowing rice-paste strokes with a curling vine around them and a sheaf of paddy above'},
    ],
    styles: [
        {key: 'outline', label: 'Trace outline', style: 'a tracing outline in a thin uniform line, every motif left open to fill with paste or powder', palette: 'black ink only', framing: 'the feet centered on the page with a white margin'},
        {key: 'stencil', label: 'Cut stencil', style: 'a cut-out stencil, each opening drawn as a solid black shape held together by white bridges', palette: 'black ink only', framing: 'the feet centered on the page with a wide white margin for cutting'},
        {key: 'alta', label: 'Alta red', style: 'a printed design in the alta manner, the soles in flat solid red with the inner motifs left as white lines', palette: 'flat alta red with fine white detail lines', framing: 'the feet centered on the page with a white margin'},
    ],
    directions: ['with kalash', 'rangoli border', 'small and simple'],
    directionHint: 'try: with kalash',
    seo: {
        title: 'Lakshmi footprints: pair of feet, path, alpana feet',
        metaDescription: 'Printable Lakshmi footprint designs for Lakshmi Puja: a pair of feet, a path to the door and an alpana pair, to trace or cut as a stencil.',
        h1: 'Lakshmi footprints: pair of feet, path inside, alpana feet',
        tagline: 'Nine footprint designs to trace or cut, in an outline, a stencil and an alta-red version.',
        intro: 'The small footprints of the goddess Lakshmi are drawn at the door and leading inside on Lakshmi Puja, at Diwali or Kojagari Lakshmi Puja, in rice paste, alta or rangoli powder. Each picture is a pair of feet, a path of six footprints climbing to a doorway, or a Bengali alpana pair with a curling vine. The styles are a tracing outline, a cut stencil and a red alta print.',
        useCases: [
            'a path of footprints to the door on Lakshmi Puja',
            'a stencil for the threshold',
            'a design for an alta or rice-paste floor',
            'a tracing sheet for a family puja',
        ],
        faq: [
            {q: 'Are these real feet?', a: 'No. The feet are stylised, with rounded toes, drawn flat on a white sheet.'},
            {q: 'What is the stencil style for?', a: 'Each opening is a solid black shape held by white bridges, so the paste or powder falls only through the openings.'},
            {q: 'What materials are these designs for?', a: 'The pictures are designs for rice paste, alta or rangoli powder, the materials the tradition uses at the door.'},
            {q: 'Can I ask for a different design?', a: 'Yes. The direction field takes a change such as with kalash, a rangoli border or small and simple. A direction you type is a request, not a promised result.'},
        ],
    },
}
